import { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../ContractSearch.styles";
import { fill } from "../ContractSearchHelper";
import {
  AiMessage,
  ChatMessage,
  CompareLock,
  MatchedContract,
  MatchedPassage,
} from "../ContractSearchTypes";
import AnswerActions from "./AnswerActions";
import { contractsNamedInHeadings } from "../compareEntryMatching";
import AnswerCard from "./AnswerCard";
import { SparkGlyph } from "./ContractSearchIcons";
import FollowUpChips from "./FollowUpChips";
import MatchedContractsList from "./MatchedContractsList";

interface ChatThreadProps {
  messages: ChatMessage[];
  totalIndexed: number;
  isSending: boolean;
  expanded: Record<string, boolean>;
  onToggle: (messageId: string, documentId: string) => void;
  activePassageKey: string | null;
  onOpenPassage: (passage: MatchedPassage, contract: MatchedContract, passageKey: string) => void;
  onAsk: (text: string) => void;
  onCopy: (message: AiMessage) => void;
  onRetry: (message: AiMessage) => void;
  /** Opens Compare contract versions for a matched contract; passed on to every "Matched contracts" list. */
  onCompare?: (contract: MatchedContract) => void;
  /** Set when the plan lacks AI Assistant: the compare controls look locked. */
  compareLock?: CompareLock | null;
}

const Thinking = ({ totalIndexed }: { totalIndexed: number }) => (
  <Box sx={styles.thinking} role="status">
    <Box sx={styles.thinkingDot(0)} aria-hidden="true" />
    <Box sx={styles.thinkingDot(0.18)} aria-hidden="true" />
    <Box sx={styles.thinkingDot(0.36)} aria-hidden="true" />
    <Typography component="span" sx={styles.thinkingText}>
      {fill(strings.contractSearch.thinking, { count: totalIndexed })}
    </Typography>
  </Box>
);

/**
 * The conversation. It is a `role="log"` live region so a screen reader hears
 * each new answer once, without the whole thread being re-read.
 */
const ChatThread = ({
  messages,
  totalIndexed,
  isSending,
  expanded,
  onToggle,
  activePassageKey,
  onOpenPassage,
  onAsk,
  onCopy,
  onRetry,
  onCompare,
  compareLock,
}: ChatThreadProps) => {
  const endRef = useRef<HTMLDivElement | null>(null);
  const lastCount = useRef(0);

  // Scroll to the newest turn when one is added or when the pending turn settles.
  const settledCount = messages.filter(
    (m) => m.role === "user" || !m.thinking,
  ).length;
  useEffect(() => {
    if (settledCount !== lastCount.current) {
      lastCount.current = settledCount;
      endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [settledCount]);

  return (
    <Box sx={styles.thread} role="log" aria-live="polite" aria-relevant="additions">
      {messages.map((message) => {
        if (message.role === "user") {
          return (
            <Box key={message.id} sx={styles.userRow}>
              <Box sx={styles.userBubble}>{message.text}</Box>
            </Box>
          );
        }

        const turn = message.turn;
        return (
          <Box key={message.id} sx={styles.aiRow}>
            <Box sx={styles.aiAvatar} aria-hidden="true">
              <SparkGlyph sx={{ width: 15, height: 15 }} />
            </Box>
            <Box sx={styles.aiBody}>
              {message.thinking || !turn ? (
                <Thinking totalIndexed={totalIndexed} />
              ) : (
                <>
                  <AnswerCard
                    turn={turn}
                    totalIndexed={totalIndexed}
                    contracts={turn.kind === "answer" ? turn.contracts : undefined}
                    onCompare={onCompare}
                    compareLock={compareLock}
                    idPrefix={message.id}
                  />
                  {turn.kind === "answer" && (
                    <MatchedContractsList
                      idPrefix={message.id}
                      contracts={turn.contracts}
                      totalIndexed={totalIndexed}
                      expanded={Object.fromEntries(
                        turn.contracts.map((c) => [
                          c.documentId,
                          !!expanded[`${message.id}:${c.documentId}`],
                        ]),
                      )}
                      onToggle={(documentId) => onToggle(message.id, documentId)}
                      activePassageKey={activePassageKey}
                      onOpenPassage={onOpenPassage}
                      onCompare={onCompare}
                      compareLock={compareLock}
                      compareSkipIds={contractsNamedInHeadings(turn.answer, turn.contracts)}
                    />
                  )}
                  {turn.kind !== "error" && (
                    <FollowUpChips
                      questions={turn.followUps}
                      variant={
                        turn.kind === "answer" || turn.followUpsFromAi
                          ? "followUps"
                          : "tryInstead"
                      }
                      onSelect={onAsk}
                      disabled={isSending}
                    />
                  )}
                  {(turn.kind === "answer" || turn.kind === "error") && (
                    <AnswerActions
                      onCopy={() => onCopy(message)}
                      onRetry={() => onRetry(message)}
                      retryDisabled={isSending}
                      showCopy={turn.kind === "answer"}
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        );
      })}
      <div ref={endRef} />
    </Box>
  );
};

export default ChatThread;
